import { call, put, select, take } from 'typed-redux-saga/macro';

import { getRoute } from 'Config';
import type { GetActionByType } from 'Lib/reduxType';

import { logout } from 'Store/commonActions';
import { verifyRouteSuccess } from 'Store/loading/actions';
import { submitAccountRecoveryOtp, submitMagicLinkOtp } from 'Store/mfa/actions';
import { LOGOUT_SUCCESS } from 'Store/mfa/constants';
import { navigate } from 'Store/routing/actions';
import { setDeeplinkVerificationInfo, storePostLoginRedirectPath } from 'Store/user/actions';
import {
  DEEPLINK_TYPE_ACCOUNT_RECOVERY,
  DEEPLINK_TYPE_CAMPAIGN,
  DEEPLINK_TYPE_MAGIC_LINK_SELECT,
  DEEPLINK_TYPE_MAGIC_LINK_WEB_DISCOUNT,
} from 'Store/user/constants';
import { getDeepLinkOtp, getIsUserLoggedIn } from 'Store/user/selectors';

import { VERIFY_CONFIRMATION_DEEPLINK_ROUTE } from '../constants';
import { handleInitialRouting } from './common';

// Account recovery
export function* verifyEmailLoginDeeplink() {
  const isLoggedIn = yield* select(getIsUserLoggedIn);

  const otp = yield* select(getDeepLinkOtp);
  if (!isLoggedIn) {
    yield* put(
      navigate(getRoute('login', { pathConfig: { withPrefix: false } }), { replace: true })
    );
  } else {
    // For account recovery, we need to log the current user out
    // if they are already logged in
    yield* put(logout());
    yield* take(LOGOUT_SUCCESS);
  }
  if (otp) {
    yield* put(submitAccountRecoveryOtp(otp));
  }
  yield* put(verifyRouteSuccess());
}

/**
 * This handler will verify if magic token is valid.
 */
export const verifyMagicTokenDeeplink = (deeplinkType, postLoginRedirectPath) =>
  function* magicLinkTokenDeeplinkHandler() {
    const isLoggedIn = yield* select(getIsUserLoggedIn);

    const otp = yield* select(getDeepLinkOtp);
    if (isLoggedIn) {
      // For account recovery, we need to log the current user out
      // if they are already logged in
      yield* put(navigate(postLoginRedirectPath));
    } else {
      yield* put(
        storePostLoginRedirectPath({
          path: postLoginRedirectPath,
          pushState: true,
        })
      );
      yield* put(
        navigate(
          getRoute('login', {
            pathConfig: { withPrefix: false },
          }),
          { replace: true }
        )
      );
      if (otp) {
        yield* put(submitMagicLinkOtp(otp, deeplinkType));
      }
    }

    yield* put(verifyRouteSuccess());
  };

const confirmationDeeplinkMapping = {
  [DEEPLINK_TYPE_ACCOUNT_RECOVERY]: verifyEmailLoginDeeplink,
  [DEEPLINK_TYPE_CAMPAIGN]: verifyEmailLoginDeeplink,
  [DEEPLINK_TYPE_MAGIC_LINK_SELECT]: verifyMagicTokenDeeplink(
    DEEPLINK_TYPE_MAGIC_LINK_SELECT,
    getRoute('tinderSelect', {
      pathConfig: { withPrefix: false },
    })
  ),
  [DEEPLINK_TYPE_MAGIC_LINK_WEB_DISCOUNT]: verifyMagicTokenDeeplink(
    DEEPLINK_TYPE_MAGIC_LINK_WEB_DISCOUNT,
    getRoute('subscriptionSettings')
  ),
};

export function* verifyConfirmationDeeplinkRouteSaga(
  action: GetActionByType<typeof VERIFY_CONFIRMATION_DEEPLINK_ROUTE>
) {
  yield* put(setDeeplinkVerificationInfo(action.payload));

  const { type } = action.payload;
  const handler = confirmationDeeplinkMapping[type];
  if (handler) {
    yield* call(handler, action);
  } else {
    yield* call(handleInitialRouting);
  }
}
